import type { OrgRoles, Roles } from "@aura-frontend/omnibus-az-client";
import {
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Navigate,
  useLocation,
  useMatches,
  useNavigate,
  useNavigation,
  useNavigationType,
} from "react-router-dom";

import smartparkLogo from "@/assets/smartpark-logo-white.png";
import winWLogo from "@/assets/win-w-logo-white.svg";
import { useAppContext } from "@/hooks/use-app-context";
import { useIsMobile } from "@/hooks/use-mobile";
import { getHomeRoute } from "@/lib/permissions";
import { ROUTES } from "@/lib/routes";
import { removeOrgPrefix } from "@/lib/utils";
import { RoleOptions, UserRoleOptions } from "@/types/roles";
import type { RouteHandle } from "@/types/routes";

import GlobalSpinner from "../ui/global-spinner";
import {
  NavMenu,
  NavMenuGroup,
  type NavMenuItemData,
  NavMenuProvider,
  SIDEBAR_WIDTH_COLLAPSED,
  SIDEBAR_WIDTH_EXPANDED,
} from "../ui/SideMenu";
import NavBar from "./NavBar";

interface LayoutProps {
  children: ReactNode;
  authRequired?: boolean;
}

const LOGIN_PATH = "/login";

type NavItemConfig = {
  id: string;
  title: string;
  url?: string;
  permissions?: Array<keyof typeof RoleOptions | keyof typeof UserRoleOptions>;
  excludedRoles?: Array<
    keyof typeof RoleOptions | keyof typeof UserRoleOptions
  >;
  items?: Omit<NavItemConfig, "items">[];
};

const navItems: NavItemConfig[] = [
  {
    id: "my-smartparks",
    title: "My SmartParks",
    url: ROUTES.MANAGE_SMARTPARKS,
    permissions: [
      "staff_admin",
      "staff",
      "account_owner",
      "magnus_super_admin",
      "magnus_admin",
    ],
  },
  {
    id: "my-smartparks-old",
    title: "My SmartParks Admin",
    url: ROUTES.MANAGE_SMARTPARKS_ADMIN,
    permissions: ["magnus_super_admin", "magnus_admin"],
  },
  // Events group
  {
    id: "events",
    title: "Events",
    permissions: [
      "magnus_super_admin",
      "magnus_admin",
      "staff_admin",
      "staff",
      "account_owner",
    ],
    items: [
      {
        id: "event-schedule",
        title: "Event Schedule",
        url: ROUTES.EVENT_SCHEDULE,
        permissions: [
          "magnus_super_admin",
          "magnus_admin",
          "staff_admin",
          "staff",
          "account_owner",
        ],
      },
      {
        id: "history-logs",
        title: "History & Logs",
        url: ROUTES.EVENT_LOG,
        permissions: [
          "magnus_super_admin",
          "magnus_admin",
          "staff_admin",
          "staff",
          "account_owner",
        ],
      },
    ],
  },
  // Gameday group
  {
    id: "gameday",
    title: "Gameday",
    permissions: [
      "magnus_super_admin",
      "magnus_admin",
      "tagger",
      "staff_admin",
      "staff",
      "account_owner",
    ],
    items: [
      {
        id: "scorekeeping-v2",
        title: "Scorekeeping - NEW",
        url: ROUTES.TAGGING_V2,
        permissions: ["magnus_super_admin", "magnus_admin", "tagger"],
      },
      {
        id: "scorekeeping",
        title: "Scorekeeping",
        url: ROUTES.TAGGING,
        permissions: ["magnus_super_admin", "magnus_admin", "tagger"],
      },
      {
        id: "live-tracking",
        title: "Live Tracking",
        url: ROUTES.LIVE_TRACKING,
        permissions: ["staff_admin", "staff", "account_owner"],
      },
      {
        id: "challenge-mode",
        title: "Challenge Mode",
        url: ROUTES.CHALLENGE_MODE,
        permissions: ["staff_admin", "staff", "account_owner"],
      },
    ],
  },
  // Roster Stats
  {
    id: "roster-stats",
    title: "Roster Stats",
    url: ROUTES.DASHBOARD,
    permissions: ["staff_admin", "staff", "account_owner"],
  },
  {
    id: "manage",
    title: "Manage",
    // Admin roles are account-wide, so admins keep Manage even when the
    // current org only grants them tagger
    permissions: [
      "magnus_super_admin",
      "magnus_admin",
      "account_owner",
      "staff_admin",
      "staff",
    ],
    items: [
      {
        id: "manage-roles",
        title: "Operators",
        url: ROUTES.MANAGE_OPERATORS,
      },
      {
        id: "manage-rosters",
        title: "Rosters",
        url: ROUTES.MANAGE_ROSTERS,
      },
      {
        id: "manage-players",
        title: "Players",
        url: ROUTES.MANAGE_PLAYERS,
      },
      {
        id: "admin-organizations",
        title: "Organizations",
        url: ROUTES.MANAGE_ORGANIZATIONS,
        permissions: ["magnus_super_admin", "magnus_admin"],
      },
      {
        id: "manage-billing",
        title: "Billing & Transactions",
        url: ROUTES.MANAGE_BILLING,
        excludedRoles: ["magnus_admin", "magnus_super_admin"],
      },
    ],
  },
  {
    id: "admin-tools",
    title: "Admin Tools",
    permissions: ["magnus_super_admin", "magnus_admin"],
    items: [
      {
        id: "admin-admins",
        title: "Admins",
        url: ROUTES.ADMIN_TOOLS_ADMINS,
      },
      {
        id: "admin-verifications",
        title: "Account Verifications",
        url: ROUTES.ADMIN_TOOLS_ACCOUNT_VERIFICATION,
      },
      {
        id: "admin-csv",
        title: "Find CSV",
        url: ROUTES.ADMIN_TOOLS_FIND_CSV,
      },
    ],
  },
];

// Convert NavItemConfig to NavMenuItemData format
function convertToMenuItems(
  items: NavItemConfig[],
  orgId: string | null,
): NavMenuItemData[] {
  const cleanId = orgId ? removeOrgPrefix(orgId) : "";
  return items.map((item) => ({
    id: item.id,
    label: item.title,
    url: item.url?.replace(":organizationId", encodeURIComponent(cleanId)),
    children: item.items?.map((subItem) => ({
      id: subItem.id,
      label: subItem.title,
      url: subItem.url?.replace(":organizationId", encodeURIComponent(cleanId)),
    })),
  }));
}

// Get user initials from name
function getInitials(name: string): string {
  return name
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);
}

const Layout: React.FC<LayoutProps> = ({ authRequired, ...props }) => {
  const navigation = useNavigation();
  const navigationType = useNavigationType();
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, currentOrganization, orgId } = useAppContext();
  const mainRef = useRef<HTMLElement>(null);
  const scrollPositions = useRef<Map<string, number>>(new Map());

  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);

  // Check route handle for layout options
  // Look at deepest match first, then walk up the tree for inherited properties
  const matches = useMatches();
  const currentHandle = matches[matches.length - 1]?.handle as
    | RouteHandle
    | undefined;
  const hideNavbar =
    currentHandle?.hideNavbar ??
    [...matches].reverse().some((m) => (m.handle as RouteHandle)?.hideNavbar) ??
    false;
  const routeSidebarCollapsed = currentHandle?.sidebarCollapsed ?? false;

  // Sync sidebar collapsed state with route default (resets on navigation)
  useEffect(() => {
    setIsSidebarCollapsed(routeSidebarCollapsed);
  }, [routeSidebarCollapsed]);

  // Scroll restoration: save scroll position before navigating away,
  // restore on POP (back/forward), scroll to top on PUSH/REPLACE
  const previousKey = useRef(location.key);
  useEffect(() => {
    const main = mainRef.current;
    const savedPositions = scrollPositions.current;
    if (!main) return;

    if (navigationType === "POP") {
      const saved = savedPositions.get(location.key);
      if (saved != null) {
        main.scrollTop = saved;
      }
    } else {
      main.scrollTop = 0;
    }

    return () => {
      savedPositions.set(previousKey.current, main.scrollTop);
      previousKey.current = location.key;
    };
  }, [location.key, navigationType]);

  // navbarClassName/mainClassName can be set on a parent layout route and inherited by children
  const resolvedNavbarClassName = [...matches]
    .reverse()
    .find((m) => (m.handle as RouteHandle)?.navbarClassName)?.handle as
    | RouteHandle
    | undefined;
  const resolvedMainClassName = [...matches]
    .reverse()
    .find((m) => (m.handle as RouteHandle)?.mainClassName)?.handle as
    | RouteHandle
    | undefined;

  const currentRoles = useMemo(
    () =>
      [...(user?.roles || []), ...(currentOrganization?.orgRoles || [])] as (
        | Roles
        | OrgRoles
      )[],
    [user?.roles, currentOrganization?.orgRoles],
  );

  const { logout: supabaseLogout, isAuthenticated } = useAppContext();

  const handleLogout = useCallback(async () => {
    localStorage.setItem("explicit-logout", "true");

    try {
      // Logout from Supabase
      await supabaseLogout();
    } catch (e) {
      console.error("[Layout] Supabase logout error:", e);
    }
  }, [supabaseLogout]);

  // Filter navigation items based on permissions
  const filteredNavItems = useMemo(() => {
    const hasPermission = (
      item: NavItemConfig | Omit<NavItemConfig, "icon" | "items">,
    ) => {
      if (item.permissions) {
        return item.permissions.some((permission) =>
          currentRoles.includes(permission),
        );
      }
      return true;
    };

    const isExcluded = (
      item: NavItemConfig | Omit<NavItemConfig, "icon" | "items">,
    ) => {
      if ("excludedRoles" in item && item.excludedRoles) {
        return item.excludedRoles.some((role) => currentRoles.includes(role));
      }
      return false;
    };

    return navItems
      .filter((item) => hasPermission(item) && !isExcluded(item))
      .map((item) => ({
        ...item,
        items: item.items?.filter(
          (subItem) => hasPermission(subItem) && !isExcluded(subItem),
        ),
      }));
  }, [currentRoles]);

  // Convert to SideMenuItemData format
  const menuItems = useMemo(
    () => convertToMenuItems(filteredNavItems, orgId),
    [filteredNavItems, orgId],
  );

  // Determine active item based on current route (prefix matching, longest match wins)
  const activeId = useMemo(() => {
    const currentPath = location.pathname;
    let bestMatch: { id: string; length: number } | undefined;

    const checkMatch = (id: string, url: string | undefined) => {
      if (!url) return;
      const normalizedUrl = url.replace(/\/+$/, "");
      const normalizedPath = currentPath.replace(/\/+$/, "");
      if (
        normalizedPath === normalizedUrl ||
        normalizedPath.startsWith(normalizedUrl + "/")
      ) {
        if (!bestMatch || normalizedUrl.length > bestMatch.length) {
          bestMatch = { id, length: normalizedUrl.length };
        }
      }
    };

    for (const item of menuItems) {
      checkMatch(item.id, item.url);
      if (item.children) {
        for (const child of item.children) {
          checkMatch(child.id, child.url);
        }
      }
    }
    return bestMatch?.id;
  }, [location.pathname, menuItems]);

  // User data for sidebar footer
  const userData = useMemo(() => {
    if (!user) return undefined;
    const fullName = [user.givenName, user.familyName]
      .filter(Boolean)
      .join(" ");
    const email = user.emails?.[0] || "";
    const displayName = fullName || email || "User";
    return {
      name: displayName,
      initials: getInitials(displayName),
      onLogout: handleLogout,
    };
  }, [user, handleLogout]);

  if (authRequired && !isAuthenticated) {
    return (
      <Navigate
        to={LOGIN_PATH}
        state={{
          from: `${location.pathname}${location.search}${location.hash}`,
        }}
        replace
      />
    );
  }

  return (
    <NavMenuProvider
      open={isSidebarOpen}
      onOpenChange={setIsSidebarOpen}
      collapsed={isSidebarCollapsed}
      onCollapsedChange={setIsSidebarCollapsed}
    >
      <div className="light flex h-screen overflow-hidden bg-background">
        {navigation.state === "loading" && <GlobalSpinner />}

        {/* Sidebar - fixed on desktop, drawer on mobile */}
        {isAuthenticated && (
          <NavMenu
            onLogoClick={() => navigate(getHomeRoute(currentRoles))}
            logo={
              <img
                src={smartparkLogo}
                alt="SmartPark"
                className="h-11 w-auto"
              />
            }
            collapsedLogo={
              <img
                src={winWLogo}
                alt="WIN Reality"
                className="mx-auto h-8 w-auto"
              />
            }
            user={userData}
          >
            <NavMenuGroup
              items={menuItems}
              activeId={activeId}
              onNavigate={(url) =>
                navigate(url, { state: { fromSidebar: true } })
              }
            />
          </NavMenu>
        )}

        {/* Main content area */}
        <div
          className="flex flex-1 flex-col overflow-hidden transition-[margin-left] duration-[250ms] ease-in-out"
          style={{
            marginLeft:
              isAuthenticated && !isMobile
                ? isSidebarCollapsed
                  ? SIDEBAR_WIDTH_COLLAPSED
                  : SIDEBAR_WIDTH_EXPANDED
                : "0px",
          }}
        >
          {!hideNavbar && (
            <NavBar
              className={resolvedNavbarClassName?.navbarClassName}
              onToggleSidebar={() => {
                setIsSidebarOpen(!isSidebarOpen);
              }}
            />
          )}
          <main
            ref={mainRef}
            className={`flex-1 overflow-y-auto ${resolvedMainClassName?.mainClassName ?? ""}`}
          >
            {props.children}
          </main>
        </div>
      </div>
    </NavMenuProvider>
  );
};

export default Layout;
